/**
 * Dev-only floating panel that shows the most recent spam-protection
 * decisions across every form on the site. Renders nothing in production.
 *
 * Each entry shows: decision tag, source, elapsedMs, and (if applicable)
 * the matched whitelist or spam pattern. Subscribes to spamDebugLog so
 * new submit attempts appear instantly.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import {
  clearSpamEvents,
  getSpamEvents,
  subscribeSpamEvents,
  type SpamDebugEvent,
} from "@/hooks/spamDebugLog";

const isDev = import.meta.env.DEV;

const decisionTone: Record<SpamDebugEvent["decision"], string> = {
  honeypot: "border-destructive/40 bg-destructive/10 text-destructive",
  too_fast: "border-destructive/40 bg-destructive/10 text-destructive",
  keyword_match: "border-destructive/40 bg-destructive/10 text-destructive",
  whitelist_pass: "border-primary/30 bg-primary/5 text-primary",
  learned_pass: "border-primary/30 bg-primary/5 text-primary",
  no_match_pass: "border-charcoal-foreground/15 bg-charcoal-foreground/5 text-charcoal-foreground/80",
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour12: false });
}

const SpamDebugSummary = () => {
  const events = useSyncExternalStore(subscribeSpamEvents, getSpamEvents, getSpamEvents);
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Auto-open the first time an event arrives so the developer notices it.
  const hasEvents = events.length > 0;
  useEffect(() => {
    if (hasEvents && !hidden) setOpen(true);
  }, [hasEvents, hidden]);

  if (!isDev) return null;
  if (hidden) return null;

  return (
    <div
      className="fixed bottom-3 right-3 z-[9999] max-w-[420px] w-[92vw] sm:w-[420px] font-mono text-xs"
      data-testid="spam-debug-summary"
    >
      <div className="rounded-lg border border-charcoal-foreground/15 bg-charcoal text-charcoal-foreground shadow-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 border-b border-charcoal-foreground/10 hover:bg-charcoal-foreground/5"
          aria-expanded={open}
        >
          <span className="font-semibold tracking-wide">
            [dev] spam decisions ({events.length})
          </span>
          <span className="flex items-center gap-2 opacity-70">
            <span>{open ? "−" : "+"}</span>
          </span>
        </button>

        {open && (
          <>
            <div className="max-h-[40vh] overflow-y-auto divide-y divide-charcoal-foreground/8">
              {events.length === 0 && (
                <div className="px-3 py-4 opacity-60">
                  No submissions yet. Submit a form to see decisions appear here.
                </div>
              )}
              {events.map((e) => (
                <div key={e.id} className={`px-3 py-2 border-l-2 ${decisionTone[e.decision]}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold">{e.decision}</span>
                    <span className="opacity-60">{formatTime(e.ts)}</span>
                  </div>
                  <div className="opacity-80">
                    source: {e.source} · elapsed: {e.elapsedMs}ms
                  </div>
                  {e.matchedPattern && (
                    <div className="opacity-70 break-all">spam: {e.matchedPattern}</div>
                  )}
                  {e.matchedWhitelist && (
                    <div className="opacity-70 break-all">whitelist: {e.matchedWhitelist}</div>
                  )}
                  {e.matchedVariantLabel && (
                    <div className="opacity-80 break-all">
                      variant: <span className="font-semibold">{e.matchedVariantLabel}</span>
                      {e.matchedVariantText && (
                        <span className="opacity-70"> ("{e.matchedVariantText}")</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-charcoal-foreground/10 bg-charcoal-foreground/5">
              <button
                type="button"
                onClick={() => clearSpamEvents()}
                className="px-2 py-1 rounded border border-charcoal-foreground/15 hover:bg-charcoal-foreground/10"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setHidden(true)}
                className="px-2 py-1 rounded border border-charcoal-foreground/15 hover:bg-charcoal-foreground/10"
              >
                Hide for session
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SpamDebugSummary;
